import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidatorFn, Validators } from '@angular/forms';
import { validatorJson } from '@emma-helpers/emma-validators.helper';
import { formatJson } from '@emma-helpers/format.helper';
import { debounce, noop } from 'lodash';

import { EMMAFormElementComponent } from './emma-form-element.component';
import { SelectOption } from '@platform/types';

const VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAJsonEditorComponent),
  multi: true,
};

const VALIDATORS = {
  provide: NG_VALIDATORS,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAJsonEditorComponent),
  multi: true,
};

@Component({
    selector: 'emma-json-editor',
    templateUrl: './emma-json-editor.component.html',
    providers: [
        VALUE_ACCESSOR,
        VALIDATORS,
        {
            provide: EMMAFormElementComponent,
            useExisting: EMMAJsonEditorComponent,
        },
    ],
    standalone: false
})
export class EMMAJsonEditorComponent extends EMMAFormElementComponent implements OnInit, OnChanges {
  @Input() formatJson = true;
  @Input() keepFormat = false;
  @Input() validateJson = true;
  @Input() indentation = 2;
  @Input() linesToShow = 10;
  // Autocomplete
  @Input() autocompleteItems: Array<SelectOption> = [];

  @Input() autocompleteButtonLabel = '';
  @Input() autocompleteButtonIcon = '';
  @Input() autocompleteNotFound = '';

  // Validation
  @Input() maxlength = Infinity;
  @Input() minlength = 0;

  isInvalid = false;

  private _json = '';
  @Input()
  public get json() {
    return this._json;
  }
  public set json(json) {
    if (this._json !== json) {
      this._json = json;
      this.onChangeCallback(this._json);
      this.jsonChange.emit(this._json);
    }
  }
  public jsonPretty = '';
  public jsonPrettyHighlighted = '';
  @Output() jsonChange = new EventEmitter<string>();

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: string | number) => void = noop;

  doFormatJson() {
    this.jsonPretty = this.formatJson ? formatJson(this._json, this.indentation) : this._json;
    this.jsonPrettyHighlighted = this.highlightJson(this.jsonPretty);
  }
  private highlightJson(value: string): string {
    let highlighted = value;
    for (const auto of this.autocompleteItems) {
      highlighted = highlighted.replaceAll(
        String(auto.value),
        `<mark title="${auto.label}">${auto.value}</mark>`
      );
    }
    return highlighted;
  }

  /**
   * Sync the step's postbackBody with the pretty printed textarea value
   */
  onChange = (jsonPretty: string) => {
    if (this.jsonPrettyHighlighted) {
      // Remove the mark tags
      this.jsonPrettyHighlighted = '';
    }
    this.updateJsonView(jsonPretty);
  };
  // tslint:disable-next-line: member-ordering
  private updateJsonView = debounce((jsonPretty: string) => {
    if (this.jsonPretty !== jsonPretty) {
      this.jsonPretty = jsonPretty;
      this.jsonPrettyHighlighted = this.highlightJson(jsonPretty);
      this.json = this.keepFormat ? jsonPretty : formatJson(jsonPretty, 0);
      this.onTouchedCallback();
    }
  }, 200);
  onTouch() {
    this.onTouchedCallback();
  }

  // From ControlValueAccessor interface
  writeValue(value: string) {
    if (value !== this._json) {
      this._json = value;
      this.doFormatJson();
    }
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  // From Validator interface
  override validate: ValidatorFn = (control: AbstractControl) => {
    const validators: ValidatorFn[] = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    if (this.maxlength < Infinity) {
      validators.push(Validators.maxLength(this.maxlength));
    }
    if (this.minlength) {
      validators.push(Validators.minLength(this.minlength));
    }
    if (this.validateJson) {
      validators.push(validatorJson);
    }
    if (this.validateFn) {
      validators.push(this.validateFn);
    }
    if (validators.length) {
      const validator = Validators.compose(validators);
      const validationResult = validator ? validator(control) : null;
      this.isInvalid = validationResult ? true : false;
      return validationResult;
    }
    return null;
  };
}
